import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, Loader2 } from "lucide-react";

const INTER = { fontFamily: "'Inter', system-ui, sans-serif" };

export default function PortalLogin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [magicFailed, setMagicFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await portalApi.post("/portal/magic", { token });
        localStorage.setItem("fl_portal_token", data.access_token);
        navigate("/portal/jobs", { replace: true });
      } catch (e) {
        setMagicFailed(true);
        setError(e.response?.data?.detail || "This link is no longer valid.");
      }
    })();
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await portalApi.post("/portal/login", { email, password });
      localStorage.setItem("fl_portal_token", data.access_token);
      navigate("/portal/jobs", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your details.");
    } finally {
      setBusy(false);
    }
  };

  if (token && !magicFailed) {
    return (
      <div className="min-h-screen bg-[#F6F7F9] flex items-center justify-center" style={INTER}>
        <div className="flex items-center gap-3 text-zinc-500" data-testid="portal-magic-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Opening your portal…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7F9] flex items-center justify-center px-4" style={INTER}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <span className="w-10 h-10 rounded-xl bg-[#FF5F15] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" />
          </span>
          <div className="text-xl font-extrabold tracking-tight text-zinc-900">Client portal</div>
        </div>
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6" data-testid="portal-login-card">
          <h1 className="text-lg font-bold text-zinc-900">Sign in</h1>
          <p className="text-sm text-zinc-500 mt-1">Track how your jobs are progressing.</p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="mt-1.5 h-11 rounded-lg" data-testid="portal-email-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                     className="mt-1.5 h-11 rounded-lg" data-testid="portal-password-input" />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="portal-login-error">{error}</div>}
            <Button type="submit" disabled={busy}
                    className="w-full h-11 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold"
                    data-testid="portal-login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
        <p className="text-xs text-zinc-400 text-center mt-6">
          No login? Ask your contractor for a portal link.
        </p>
      </div>
    </div>
  );
}
